import React, { useCallback, useEffect, useRef, useState } from 'react';

import router from 'next/router';
import useStoreApp from '@/store';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useRouter as useRouterNext13 } from 'next13-progressbar';
import useTourBooking from '@/hooks/useTourBooking';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import { PaymentMethodEnum } from '@/containers/TourBookingByCustomer/providers/TourBookingProvider';

import { tourBookingService } from '@/services/tourBookingService';
import { paymentService } from '@/services/paymentService';
import { bookingService } from '@/services/bookingService';

import moment, { duration } from 'moment';
import { Box, CircularProgress, Grid, Modal, Typography } from '@mui/material';
import BookingInfoSection from './BookingInfoSection';
import PaymentSummarySection from './PaymentSummarySection';
import { ICreateTourBooking, ITrip } from '@/types/interface';
import { BookingType, PaymentStatus, PaymentType, SocketType, SourceType } from '@/types/consistency';
import { formatDateDisplay, formatDateRequest, formatTimeDisplay, pushToBookingDetail } from '@/utils/utils';

import {
  BookingConfirmContainer,
  BookingInfoBox,
  PaymentInfoBox,
  BookingTitle,
  PaymentTitle,
} from './style';
import { handleLocalStorage } from '@/utils/handleLocalStorage';
import { TourPriceOption } from '@/types/tourBooking';
import { hoiangoShowLog } from '@/utils/log';
import PayPalPaymentModal from '@/components/PayPalPaymentModal';
import WaitingPayment from '../WaitingPayment';
import { trackTourBookingStep } from '@/utils/gtag';

export interface PendingOrder {
  order_id: string;
  tour_id: string;
  slug: string;
  order_codeTracking: string;
  payment_status: string;
  //duration: number;
  tourPriceOptionId: string;
  tourPriceOptions: TourPriceOption[];
  bankCode?: string;
  paymentType?: string;
  vnpayUrl?: string;
}

interface BookingConfirmProps {
  pendingOrder?: PendingOrder;
  sx?: any
}

/*
const paymentMethods = [
  {
    key: 'PAYPAL',
    label: 'Paypal',
    icon: '/assets/icons/paypal.svg',
    disabled: false,
  },
  {
    key: 'VNPAY',
    label: 'VN Pay',
    icon: '/assets/icons/vnpay.svg',
    disabled: false,
  },
  {
    key: 'PAY_LATER',
    label: 'common:payLater',
    icon: '',
    disabled: false,
  },
];
*/

interface PaymentMethod {
  key: PaymentMethodEnum;
  label: string;
  icon: string;
  paymentType: string;
  bankCode?: string;
  disabled: boolean;
}

const paymentMethods: PaymentMethod[] = [
  {
    key: PaymentMethodEnum.PAYPAL,
    label: 'common:payment_methods.paypal',
    icon: '/assets/icons/payment/ic_paypal_name.png',
    paymentType: PaymentType.PAYPAL.code,
    disabled: false,
  },
  {
    key: PaymentMethodEnum.INTCARD,
    label: 'common:payment_methods.international_card',
    icon: '/assets/icons/payment/ic_international_card.png',
    paymentType: PaymentType.VNPAY.code,
    bankCode: 'INTCARD',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.VNBANK,
    label: 'common:payment_methods.domestic_and_bank',
    icon: '/assets/icons/payment/ic_atm_card.png',
    paymentType: PaymentType.VNPAY.code,
    bankCode: 'VNBANK',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.VNPAYQR,
    label: 'common:payment_methods.qr_code',
    icon: '/assets/icons/payment/ic_scan_qr.png',
    paymentType: PaymentType.VNPAY.code,
    bankCode: 'VNPAYQR',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.PAY_LATER,
    label: 'common:payLater',
    icon: '',
    paymentType: PaymentType.Cash.code,
    disabled: false,
  },
];

declare global {
  interface Window {
    paypal: any;
  }
}

const BookingConfirm = React.forwardRef<HTMLElement, BookingConfirmProps & { paymentInfoBoxRef?: React.Ref<HTMLElement> }>(
  ({ pendingOrder, sx, paymentInfoBoxRef }, ref) => {
    const { t } = useTranslation();
    const pathName = usePathname();
    const routerNext13 = useRouterNext13();
    const { collaboratorId } = router.query;

    const {
      bookingTourData,
      services,
      tourDetail,
      contactInfo,
      computedPricing,
      storePromotion,
      setBookingTourData,
      setContactInfo,
      setComputedPricing,
      showConfirmModal,
      finishRenderPaypalButtons,
      setFinishRenderPaypalButtons,
      durationOptions,
    } = useTourBookingContext();

    const {
      doSubmit,
      successBooking,
      bookingDone,
      loading,
      bookingErrMsg,
      createPaypalPayment,
      onCapturePaypalPayment,
      showPaypalPaymentModal,
      setShowPaypalPaymentModal,
      paypalOrderId,
      setPaypalOrderId,
      paypalPaymentApproved,
      setPaypalPaymentApproved,
      setBookingErrMsg,
      embedWaitingPayment,
      setEmbedWaitingPayment,
      doUpdatePaymentType,
    } =
      useTourBooking({
        service: tourBookingService,
        tourType: undefined,
        currentTour: null
      });

    const [selectedPayment, setSelectedPayment] = useState<string>(PaymentMethodEnum.PAY_LATER);
    const { languageId, inPayment, setInPayment, isEmbeed } = useStoreApp(state => state.globalSlice);

    const [showMBBankPaymentModal, setShowMBBankPaymentModal] = useState<boolean>(false);
    const [isMBBankProcessing, setIsMBBankProcessing] = useState<boolean>(false);
    const [mbBankPaymentURL, setMBBankPaymentURL] = useState<string>("");
    const [forceClosePaypalPaymentModal, setForceClosePaypalPaymentModal] = useState<boolean>(false);
    const [orderConfirmed, setOrderConfirmed] = useState<PendingOrder | undefined>(undefined);

    const paypalRef = useRef<HTMLDivElement>(null);

    function storeOrderConfirm(orderConfirm: PendingOrder) {
      setOrderConfirmed(orderConfirm);
      handleLocalStorage.set('order_confirm', JSON.stringify(orderConfirm));
    }

    const createAndStoreConfirmOrder = () => {

      // Store order info in localStorage before redirect
      if (tourDetail && bookingDone.codeTracking) {

        let haveBankCode = selectedPayment != PaymentMethodEnum.PAYPAL && selectedPayment != PaymentMethodEnum.PAY_LATER;

        let orderConfirmed: PendingOrder = {
          order_id: bookingDone.id,
          tour_id: tourDetail.id,
          slug: tourDetail.slug,
          order_codeTracking: bookingDone.codeTracking,
          payment_status: 'waiting',
          //duration: bookingTourData.duration,
          tourPriceOptionId: bookingTourData.tourPriceOptionId,
          tourPriceOptions: tourDetail.tourPriceOptions,
          paymentType: bookingDone.paymentType,
          bankCode: haveBankCode ? selectedPayment : undefined
        };
        storeOrderConfirm(orderConfirmed);

        return orderConfirmed;
      }

      return undefined;
    }

    const onVNPayPayment = useCallback(async (bankCode: string) => {
      if (bookingDone.id !== null) {

        let orderConfirm: PendingOrder | undefined = createAndStoreConfirmOrder();

        const bodyVNPay = {
          id: bookingDone.id,
          paymentType: PaymentType.VNPAY.code,
          bankCode: bankCode
        } as {
          id: string;
          paymentType: string;
          bankCode?: string;
        };

        const res = await paymentService.getVNPayUrl(bodyVNPay);

        const vnpayUrl = res.data.vnpUrlReturn;

        if (vnpayUrl) {
          if (orderConfirm) {
            orderConfirm.vnpayUrl = vnpayUrl;
            storeOrderConfirm(orderConfirm);
          }

          if (isEmbeed) {
            setEmbedWaitingPayment(true);
          }
          window.open(vnpayUrl, isEmbeed ? '_blank' : '_self');
        }
      }
    }, [bookingDone, tourDetail]);


    function onMBBankPayment() {
      if (bookingDone.id) {
        setIsMBBankProcessing(true);

        const res = paymentService.getMBBankUrl(bookingDone.id);

        res.then((rs: any) => {
          if (rs.data) {
            setMBBankPaymentURL(rs.data.paymentUrl);
            setShowMBBankPaymentModal(true);
          }
        })
      }
    };

    const renderPaypalButtons = () => {
      try {
        if (typeof window !== 'undefined' && window.paypal) {
          window.paypal.Buttons({
            createOrder: async function (data: any, actions: any) {
              const orderId = await createPaypalPayment();
              setPaypalOrderId(orderId);
              return orderId;
            },
            onApprove: async function (data: any, actions: any) {
              const res = await onCapturePaypalPayment();
              if (res) {
                setShowPaypalPaymentModal(false);
                setPaypalPaymentApproved(true);
                setTimeout(() => {
                  pushToBookingDetail(router, bookingDone.codeTracking);
                }, 1000);
              }
            },
          })
            .render(paypalRef?.current) // '#paypal-button'
            .then(() => {
              if (!finishRenderPaypalButtons && setFinishRenderPaypalButtons) {
                setFinishRenderPaypalButtons(true);
              }
            });
        }
        else {
          console.log('window.paypal not found');
        }
      }
      catch (error) {
        console.group('renderPaypalButtons -> error');
        console.log(error);
        console.groupEnd();
      }
    };

    useEffect(() => {
      const { display } = sx;
      if (display === 'none' && !showConfirmModal) {
        setBookingErrMsg('');
      }
    }, [sx.display])

    useEffect(() => {
      const { display } = sx;

      if (display === 'none') return;

      if (!inPayment) {
        setInPayment(true);
      }

      return () => {
        setInPayment(false);
      };
    }, [sx.display]);

    useEffect(() => {

      if (!successBooking) return;

      switch (selectedPayment) {
        case PaymentMethodEnum.PAYPAL:
          // TODO: re-check forece close here !!!
          if (forceClosePaypalPaymentModal) return;

          createAndStoreConfirmOrder();
          setShowPaypalPaymentModal(true);
          break;

        case PaymentMethodEnum.VNPAY:
        case PaymentMethodEnum.INTCARD:
        case PaymentMethodEnum.VNBANK:
        case PaymentMethodEnum.VNPAYQR:
          onVNPayPayment(selectedPayment);
          break;

        case PaymentMethodEnum.PAY_LATER:
          if (bookingDone.codeTracking) {
            pushToBookingDetail(router, bookingDone.codeTracking);
          }
          break;

        case PaymentMethodEnum.MBBANK:
          onMBBankPayment();
          break;

        default:
          // Default logic
          break;
      }
    }, [successBooking, pushToBookingDetail, forceClosePaypalPaymentModal]);

    // Effect: If pendingOrder, check payment status
    useEffect(() => {

      const checkPendingOrder = async () => {
        if (pendingOrder && pendingOrder.order_codeTracking) {
          try {
            const { data }: any = await bookingService.getDetailBooking(pendingOrder.order_codeTracking);

            if ((data.paymentType === PaymentType.VNPAY.code ||
              data.paymentType === PaymentType.PAYPAL.code) &&
              data.paymentStatus === PaymentStatus.Paid.code) {
              handleLocalStorage.remove('order_confirm');
            } else {
              // Load order detail data into fields
              if (data) {
                // Map data fields to bookingTourData and passenger as needed
                setComputedPricing((prev) => ({
                  ...prev,
                  price: data.price ?? 0,
                  discount: data.discount ?? 0,
                  discount_percent: data.discount_percent ?? 0,
                  discount_price: data.discount ?? 0,
                  visitPlaceTicketFee: data.visitPlaceTicketFee ?? 0,
                  additional_price: data.additionalPrice ?? 0,
                  money: data.money ?? 0,
                  pureTourPrice: data.pureTourPrice ?? 0,
                  additional_service_list: data.additional_service_list ?? [],
                  visit_place_list: data.visit_place_list ?? [],
                  additional_passenger_price: data.additional_passenger_price ?? 0,
                }));

                setBookingTourData((prev) => ({
                  ...prev,
                  tourId: data.tourId,
                  tourPriceOptionId: data.tourPriceOptionId,
                  date: data.scheduleDate ? moment(data.scheduleDate) : prev.date,
                  time: data.scheduleDate ? moment(data.scheduleDate) : prev.time,
                  additionalServiceIds: data.additionalServiceIds || [],
                  pickUpAddress: data.pickUpAddress || '',
                  pickUp: data.pickUp || undefined,
                  passengerNormal: data.passengerNormal || 0,
                  passengerHalf: data.passengerHalf || 0,
                  note: data.note || '',
                  promotionDetailId: data.promotionDetailId,
                  promotionId: data.promotionId,
                  collaboratorId: data.collaboratorId,
                }));

                setContactInfo({
                  fullName: data.passenger?.fullName || '',
                  phone: data.passenger?.phone || '',
                  email: data.passenger?.email || '',
                  zalo: data.passenger?.zalo || undefined,
                  whatsapp: data.passenger?.whatsapp || undefined,
                  kakaotalk: data.passenger?.kakaotalk || undefined,
                  viber: data.passenger?.viber || undefined,
                  dialCode: data.passenger?.dialCode || undefined,
                });

              }
            }
          } catch (e) {
            // handle error
          }
        }
      };

      if (pendingOrder) {
        const storeOrder = pendingOrder;

        if (storeOrder.bankCode) {
          setSelectedPayment(storeOrder.bankCode);
        }

        checkPendingOrder();
      }
    }, [pendingOrder, setBookingTourData, setContactInfo]);

    const handleUpdatePaymentNewOrder = useCallback(() => {

      const order = pendingOrder || orderConfirmed;

      if (!order) return;

      if (selectedPayment !== order.paymentType) {
        let paymentType = PaymentType.Cash.code

        const isVNPay = selectedPayment === PaymentMethodEnum.INTCARD ||
          selectedPayment === PaymentMethodEnum.VNBANK ||
          selectedPayment === PaymentMethodEnum.VNPAYQR ||
          selectedPayment === PaymentType.VNPAY.code ||
          selectedPayment.startsWith('VNPAY');

        if (isVNPay) {
          paymentType = PaymentType.VNPAY.code;
        }

        else if (selectedPayment === PaymentType.PAYPAL.code) {
          paymentType = PaymentType.PAYPAL.code;
        }

        doUpdatePaymentType(order.order_id, paymentType);
      }
      else if (selectedPayment === PaymentMethodEnum.INTCARD ||
        selectedPayment === PaymentMethodEnum.VNBANK ||
        selectedPayment === PaymentMethodEnum.VNPAYQR) {
        onVNPayPayment(selectedPayment);
      }
      else if (selectedPayment === PaymentMethodEnum.PAYPAL) {
        setShowPaypalPaymentModal(true);
        setForceClosePaypalPaymentModal(false);
      }
    }, [bookingDone, successBooking, showPaypalPaymentModal, forceClosePaypalPaymentModal, selectedPayment]);

    const onSubmit = useCallback(() => {

      if (pendingOrder || orderConfirmed) {
        handleUpdatePaymentNewOrder();
        return;
      }

      if (selectedPayment === PaymentType.PAYPAL.code && successBooking && !showPaypalPaymentModal && forceClosePaypalPaymentModal) {
        setShowPaypalPaymentModal(true);
        setForceClosePaypalPaymentModal(false);
        return;
      }
      else if (bookingDone) {
        setForceClosePaypalPaymentModal(false);
        return;
      }

      const parseTime = bookingTourData.time.format(formatTimeDisplay);
      const parseDate = bookingTourData.date.format(formatDateRequest);
      const selectedDateTimeGMT7 = moment(`${parseDate} ${parseTime}:00 GMT+0700`, 'YYYY-MM-DD HH:mm:ss GMT+0700');
      const scheduleDate = selectedDateTimeGMT7.toDate();
      // const selectedDateTimeGMT7 = moment(`${parseDate} ${parseTime}`, 'YYYY-MM-DD HH:mm');
      // const selectedDateTimeZ = selectedDateTimeGMT7.format('YYYY-MM-DD HH:mm:ss Z');
      //
      // const scheduleDate = new Date(`${selectedDateTimeZ}`);

      let paymentType = PaymentType.Cash.code

      const isVNPay = selectedPayment === PaymentMethodEnum.INTCARD ||
        selectedPayment === PaymentMethodEnum.VNBANK ||
        selectedPayment === PaymentMethodEnum.VNPAYQR ||
        selectedPayment === PaymentType.VNPAY.code ||
        selectedPayment.startsWith('VNPAY');

      if (isVNPay) {
        paymentType = PaymentType.VNPAY.code;
      }

      else if (selectedPayment === PaymentType.MBBANK.code) {
        paymentType = PaymentType.MBBANK.code;
      }

      else if (selectedPayment === PaymentType.PAYPAL.code) {
        paymentType = PaymentType.PAYPAL.code;
      }

      let bookingType: string = BookingType.WebCustomer.code
      let sourceType: string = SourceType.Customer.code

      if (pathName.includes('/mobile')) {
        bookingType = BookingType.AppCustomer.code

        if (collaboratorId) {
          sourceType = SourceType.Partner.code
        }
        else {
          sourceType = SourceType.Customer.code
        }
      }

      let body: ICreateTourBooking = {
        tourId: tourDetail!.id,
        //duration: bookingTourData.duration,
        tourPriceOptionId: bookingTourData.tourPriceOptionId,
        scheduleDate,
        additionalServiceIds: bookingTourData.additionalServiceIds,
        pickUpAddress: bookingTourData.pickUpAddress,
        pickUp: bookingTourData.pickUp,
        passengerNormal: bookingTourData.passengerNormal,
        passengerHalf: bookingTourData.passengerHalf ?? 0,
        note: bookingTourData.note,
        contactInfo,
        languageId,
        collaboratorId: (collaboratorId as string) ?? undefined,
        paymentType: paymentType,
        bookingType: bookingType,
        sourceType: sourceType,
        promotionId: (storePromotion && storePromotion.promotionId) ? storePromotion.promotionId : undefined,
        promotionDetailId: (storePromotion && storePromotion.promotionDetailId) ? storePromotion.promotionDetailId : undefined,
      };

      doSubmit(body);

      trackTourBookingStep(tourDetail!.id, 'confirmation');

    }, [
      bookingTourData.additionalServiceIds,
      bookingTourData.date,
      //bookingTourData.duration,
      bookingTourData.tourPriceOptionId,
      bookingTourData.note,
      bookingTourData.passengerHalf,
      bookingTourData.passengerNormal,
      bookingTourData.pickUp,
      bookingTourData.pickUpAddress,
      bookingTourData.time,
      collaboratorId,
      contactInfo,
      doSubmit,
      languageId,
      tourDetail,
      selectedPayment,
      storePromotion,
      forceClosePaypalPaymentModal,
      showPaypalPaymentModal,
      successBooking,
    ]);

    return (
      <>
        <BookingConfirmContainer sx={sx}>
          <Grid container spacing={2}>
            {/* Left: Booking Info */}
            <Grid item xs={12} md={7} order={{ xs: 1, md: 1 }}>
              <BookingInfoBox>
                <BookingTitle>
                  {t('bookingInfo')}
                </BookingTitle>
                <BookingInfoSection />
              </BookingInfoBox>
            </Grid>
            {/* Right: Payment Info */}
            <Grid item xs={12} md={5} order={{ xs: 2, md: 2 }}>
              {/* Attach the ref here */}
              <PaymentInfoBox sx={{ display: embedWaitingPayment ? 'none' : 'block' }}>
                <PaymentTitle>
                  {t('common:paymentInfo')}
                </PaymentTitle>
                {
                  isMBBankProcessing || loading ?
                    (
                      <CircularProgress />
                    ) :
                    (
                      <PaymentSummarySection
                        t={t}
                        selectedPaymentMethodRef={paymentInfoBoxRef}
                        storePromotion={storePromotion}
                        selectedPayment={selectedPayment}
                        setSelectedPayment={setSelectedPayment}
                        paymentMethods={paymentMethods}
                        bookingErrMsg={bookingErrMsg}
                        disabled={isMBBankProcessing || loading || (!forceClosePaypalPaymentModal && successBooking)}
                        onConfirm={(confirmPaymentMethod: string) => {
                          if (confirmPaymentMethod !== selectedPayment) {
                            setSelectedPayment(confirmPaymentMethod)
                          }
                          setTimeout(() => {
                            onSubmit();
                          }, 200);
                        }}
                      />
                    )
                }
              </PaymentInfoBox>

              {
                embedWaitingPayment &&
                tourDetail &&
                bookingDone &&
                <WaitingPayment orderId={bookingDone.id} codeTracking={bookingDone.codeTracking} />
              }
            </Grid>
          </Grid>
        </BookingConfirmContainer>

        <PayPalPaymentModal
          paypalRef={paypalRef}
          isOpen={showPaypalPaymentModal}
          renderPaypalButtons={renderPaypalButtons}
          onClose={() => {
            setPaypalOrderId(undefined);
            setShowPaypalPaymentModal(false)
            setForceClosePaypalPaymentModal(true)
          }}
          paypalOrderId={paypalOrderId}
          paypalPaymentApproved={paypalPaymentApproved}
        />

      </>
    );
  }
);

BookingConfirm.displayName = 'BookingConfirm';

export default BookingConfirm;
